import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AuthSender {
  ruc: string;
  coduser: string;
  clave: string;
  producto: string;
}

export interface AuthSession {
  ruc?: string;
  coduser?: string;
  id_cia?: number;
  empresa?: string;
  nombres?: string;
  idSesion?: string | number;
  codsuc?: number;
  themes?: string;
  token?: string;
  forceRunUsed?: boolean;
  [key: string]: any;
}

export interface ApiTokenResponse {
  status?: number;
  message?: string;
  object?: {
    ruc?: string;
    coduser?: string;
    token: string;
  };
}

const STORAGE_KEY = 'sql-report-session';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);

  auth(ruc: string, coduser: string, clave: string): Observable<HttpResponse<AuthSession>> {
    const sender: AuthSender = {
      ruc,
      coduser,
      clave: btoa(clave),
      producto: 'ANALYTICS'
    };
    return this.http
      .post<AuthSession>(`${environment.API_URL_LOGIN}/usuario/auth`, sender, { observe: 'response' })
      .pipe(
        tap((resp) => {
          if (resp.status === 200 && resp.body) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(resp.body));
          }
        })
      );
  }

  requestApiToken(): Observable<ApiTokenResponse> {
    const session = this.getSession();
    if (!session?.ruc || !session?.coduser || !session?.token) {
      throw new Error('Sesión inválida o sin token activo.');
    }
    const headers = new HttpHeaders({
      Authorization: `Bearer ${session.token}`
    });
    return this.http.post<ApiTokenResponse>(
      `${environment.API_URL_PUBLIC}/auth/login`,
      { ruc: session.ruc, coduser: session.coduser },
      { headers }
    );
  }

  getSession(): AuthSession | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthSession;
    } catch {
      return null;
    }
  }

  logout(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  isAuthenticated(): boolean {
    return this.getSession() !== null;
  }

  hasValidSession(): boolean {
    if (!this.isAuthenticated()) return false;
    if (this.isTokenExpired()) {
      this.logout();
      return false;
    }
    return true;
  }

  decodeTokenExp(token?: string | null): number | null {
    const raw = token ?? this.getSession()?.token;
    if (!raw) return null;
    const parts = raw.split('.');
    if (parts.length !== 3) return null;
    try {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return typeof payload?.exp === 'number' ? payload.exp : null;
    } catch {
      return null;
    }
  }

  isTokenExpired(): boolean {
    const exp = this.decodeTokenExp();
    if (exp === null) return false;
    return exp * 1000 <= Date.now();
  }

  isForceRunUsed(): boolean {
    return this.getSession()?.forceRunUsed === true;
  }

  markForceRunUsed(): void {
    const session = this.getSession();
    if (!session) return;
    session.forceRunUsed = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }
}
